import React, { useState } from 'react'
import { toast } from 'react-toastify'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Switch,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Tooltip,
    Container,
} from '@material-ui/core'
import {
    Close as CloseIcon,
    FileCopy as CopyIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Language as LanguageIcon,
} from '@material-ui/icons'
import CodeIcon from '@material-ui/icons/Code'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { GuestCard, GuestCardLink } from '../../../models/Outreach/types'
import { useGuestCardLinks } from '../../../hooks/Outreach/useGuestCardLinks'
import { useAptConfig } from '../../../hooks/useAptConfig'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: theme.spacing(2),
            maxWidth: '700px',
            width: '100%',
        },
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(3),
        borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
    },
    dialogContent: {
        padding: theme.spacing(3),
    },
    dialogActions: {
        padding: theme.spacing(2, 3),
        borderTop: '1px solid rgba(226, 232, 240, 0.5)',
    },
    linkItem: {
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: '#3b82f6',
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)',
        },
    },
    linkHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    linkLabel: {
        fontWeight: 600,
        fontSize: '0.9375rem',
        color: '#1f2937',
    },
    linkType: {
        fontSize: '0.8125rem',
        color: '#6b7280',
        marginTop: theme.spacing(0.25),
    },
    linkUrl: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
    },
    linkUrlText: {
        flex: 1,
        fontSize: '0.8125rem',
        color: '#374151',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    copyButton: {
        padding: theme.spacing(0.75),
        color: '#6b7280',
        '&:hover': {
            color: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.08)',
        },
    },
    embedButton: {
        padding: theme.spacing(0.75),
        color: '#6b7280',
        '&:hover': {
            color: '#8b5cf6',
            backgroundColor: 'rgba(139, 92, 246, 0.08)',
        },
    },
    deleteButton: {
        padding: theme.spacing(0.75),
        color: '#6b7280',
        '&:hover': {
            color: '#ef4444',
            backgroundColor: 'rgba(239, 68, 68, 0.08)',
        },
    },
    addSection: {
        borderTop: '1px solid #e5e7eb',
        paddingTop: theme.spacing(3),
        marginTop: theme.spacing(2),
    },
    addSectionTitle: {
        fontSize: '0.9375rem',
        fontWeight: 600,
        color: '#1f2937',
        marginBottom: theme.spacing(2),
    },
    addForm: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'flex-end',
    },
    addButton: {
        textTransform: 'none',
        fontWeight: 600,
        padding: theme.spacing(1.25, 2.5),
        borderRadius: '8px',
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        color: 'white',
        whiteSpace: 'nowrap',
        '&:hover': {
            background: 'linear-gradient(135deg, #2563eb, #1e40af)',
        },
        '&:disabled': {
            background: '#9ca3af',
        },
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(4, 2),
        color: '#6b7280',
    },
    inactiveBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '4px',
        backgroundColor: '#fef2f2',
        color: '#dc2626',
        fontSize: '0.6875rem',
        fontWeight: 600,
        marginLeft: theme.spacing(1),
    },
}))

interface ManageLinksModalProps {
    open: boolean
    onClose: () => void
    guestCard: GuestCard
}

export const ManageLinksModal: React.FC<ManageLinksModalProps> = ({
    open,
    onClose,
    guestCard,
}) => {
    const classes = useStyles()
    const {
        links,
        isLoadingLinks,
        createLink,
        updateLink,
        deleteLink,
    } = useGuestCardLinks(guestCard.id)
    const { unitConfigList } = useAptConfig({ unitConfigList: true })

    const [newLinkUnitConfig, setNewLinkUnitConfig] = useState<string>('')
    const [newLinkLabel, setNewLinkLabel] = useState('')

    const handleCopyUrl = (url: string) => {
        const textarea = document.createElement('textarea')
        textarea.value = url
        textarea.style.position = 'fixed'
        textarea.style.left = '0'
        textarea.style.top = '0'
        textarea.style.opacity = '0.01'
        textarea.style.width = '1px'
        textarea.style.height = '1px'
        textarea.style.zIndex = '99999'
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        textarea.setSelectionRange(0, url.length)
        document.execCommand('copy')
        document.body.removeChild(textarea)
        toast.success('Link copied to clipboard')
    }

    const handleCopyEmbedCode = (url: string) => {
        const embedCode = `<iframe src="${url}" width="100%" height="600" frameborder="0" style="border: none; border-radius: 8px;"></iframe>`
        const textarea = document.createElement('textarea')
        textarea.value = embedCode
        textarea.style.position = 'fixed'
        textarea.style.left = '0'
        textarea.style.top = '0'
        textarea.style.opacity = '0.01'
        textarea.style.width = '1px'
        textarea.style.height = '1px'
        textarea.style.zIndex = '99999'
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        textarea.setSelectionRange(0, embedCode.length)
        document.execCommand('copy')
        document.body.removeChild(textarea)
        toast.success('Embed code copied to clipboard')
    }

    const handleToggleActive = (link: GuestCardLink) => {
        updateLink.mutate({ linkId: link.id, active: !link.active })
    }

    const handleDeleteLink = (linkId: number) => {
        if (
            !window.confirm(
                'Are you sure you want to delete this link? Anyone using this URL will no longer be able to access the form.',
            )
        ) {
            return
        }
        deleteLink.mutate(linkId)
    }

    const handleCreateLink = () => {
        const request: { unit_config_id?: number | null; label?: string } = {}
        if (newLinkUnitConfig) {
            request.unit_config_id = parseInt(newLinkUnitConfig, 10)
        }
        if (newLinkLabel.trim()) {
            request.label = newLinkLabel.trim()
        }
        createLink.mutate(request, {
            onSuccess: () => {
                setNewLinkUnitConfig('')
                setNewLinkLabel('')
            },
        })
    }

    const existingUnitConfigIds = new Set(
        links?.filter((l) => l.unit_config).map((l) => l.unit_config!.id) || [],
    )

    const hasGeneralLink = links?.some((l) => l.unit_config === null) || false

    const availableUnitConfigs = (unitConfigList || []).filter(
        (uc) => !existingUnitConfigIds.has(uc.id) && uc.shows_on_schedule,
    )

    const canCreateLink = !hasGeneralLink || newLinkUnitConfig !== ''

    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.dialog}
            maxWidth="md"
            fullWidth
            disableEnforceFocus
        >
            <DialogTitle className={classes.dialogTitle} disableTypography>
                <Container
                    style={{
                        justifyContent: 'space-between',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                    }}
                >
                    <Box>
                        <Typography
                            variant="h6"
                            style={{
                                fontWeight: 'bold',
                                color: '#1f2937',
                                margin: 0,
                            }}
                        >
                            Manage Links
                        </Typography>
                        <Typography
                            variant="body2"
                            style={{ color: '#6b7280' }}
                        >
                            {guestCard.card_name}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        style={{ padding: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Container>
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
                {isLoadingLinks ? (
                    <Box display="flex" justifyContent="center" padding={4}>
                        <CircularProgress />
                    </Box>
                ) : links && links.length > 0 ? (
                    links.map((link) => (
                        <Box key={link.id} className={classes.linkItem}>
                            <Box className={classes.linkHeader}>
                                <Box>
                                    <Box display="flex" alignItems="center">
                                        <Typography
                                            className={classes.linkLabel}
                                        >
                                            {link.label || 'Untitled Link'}
                                        </Typography>
                                        {!link.active && (
                                            <span
                                                className={
                                                    classes.inactiveBadge
                                                }
                                            >
                                                Inactive
                                            </span>
                                        )}
                                    </Box>
                                    <Typography className={classes.linkType}>
                                        {link.unit_config
                                            ? `Floorplan: ${link.unit_config.name}`
                                            : 'General (all floorplans)'}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center">
                                    <Tooltip
                                        title={
                                            link.active
                                                ? 'Deactivate'
                                                : 'Activate'
                                        }
                                    >
                                        <Switch
                                            checked={link.active}
                                            onChange={() =>
                                                handleToggleActive(link)
                                            }
                                            color="primary"
                                            size="small"
                                        />
                                    </Tooltip>
                                    <Tooltip title="Delete link">
                                        <IconButton
                                            className={classes.deleteButton}
                                            size="small"
                                            onClick={() =>
                                                handleDeleteLink(link.id)
                                            }
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Box className={classes.linkUrl}>
                                <LanguageIcon
                                    style={{
                                        fontSize: '1rem',
                                        color: '#9ca3af',
                                    }}
                                />
                                <Typography className={classes.linkUrlText}>
                                    {link.public_url}
                                </Typography>
                                <Tooltip title="Copy URL">
                                    <IconButton
                                        className={classes.copyButton}
                                        size="small"
                                        onClick={() =>
                                            handleCopyUrl(link.public_url)
                                        }
                                    >
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Copy Embed Code">
                                    <IconButton
                                        className={classes.embedButton}
                                        size="small"
                                        onClick={() =>
                                            handleCopyEmbedCode(link.public_url)
                                        }
                                    >
                                        <CodeIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ))
                ) : (
                    <Box className={classes.emptyState}>
                        <LanguageIcon
                            style={{
                                fontSize: '3rem',
                                color: '#d1d5db',
                                marginBottom: 8,
                            }}
                        />
                        <Typography
                            variant="body1"
                            style={{ fontWeight: 500, color: '#374151' }}
                        >
                            No links yet
                        </Typography>
                        <Typography
                            variant="body2"
                            style={{ color: '#6b7280' }}
                        >
                            Create a link below to share this guest card on your
                            website
                        </Typography>
                    </Box>
                )}

                <Box className={classes.addSection}>
                    <Typography className={classes.addSectionTitle}>
                        Add New Link
                    </Typography>
                    <Box className={classes.addForm}>
                        <FormControl
                            variant="outlined"
                            size="small"
                            style={{ minWidth: 200, flex: 1 }}
                        >
                            <InputLabel>Floorplan (optional)</InputLabel>
                            <Select
                                value={newLinkUnitConfig}
                                onChange={(e) =>
                                    setNewLinkUnitConfig(
                                        e.target.value as string,
                                    )
                                }
                                label="Floorplan (optional)"
                            >
                                <MenuItem value="" disabled={hasGeneralLink}>
                                    <em>
                                        {hasGeneralLink
                                            ? 'General link already exists'
                                            : 'General (no floorplan)'}
                                    </em>
                                </MenuItem>
                                {availableUnitConfigs.map((uc) => (
                                    <MenuItem
                                        key={uc.id}
                                        value={uc.id.toString()}
                                    >
                                        {uc.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            variant="outlined"
                            size="small"
                            label="Label (optional)"
                            placeholder="e.g., Homepage Widget"
                            value={newLinkLabel}
                            onChange={(e) => setNewLinkLabel(e.target.value)}
                            style={{ flex: 1 }}
                        />
                        <Button
                            className={classes.addButton}
                            startIcon={<AddIcon />}
                            onClick={handleCreateLink}
                            disabled={createLink.isLoading || !canCreateLink}
                        >
                            {createLink.isLoading ? 'Creating...' : 'Create'}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    style={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontWeight: 500,
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}
